import { readStored, removeStored, writeStored } from "@/lib/safeStorage";
import { cn } from "@/lib/utils";
import { ArrowLeft, CalendarDays, Mail, PenLine, X } from "lucide-react";
import {
    memo,
    useEffect,
    useRef,
    useState,
    type PointerEvent as ReactPointerEvent,
} from "react";
import { useTranslation } from "react-i18next";
import ComposePane from "./ComposePane";
import EventPane from "./EventPane";
import MailPane from "./MailPane";
import type { PaneGroup, PaneKind, PaneTab, SidePanes } from "./sidePanes";

const KIND_ICON = {
    mail: Mail,
    event: CalendarDays,
    compose: PenLine,
} as const;

interface AssistantSidePanelProps {
    panes: SidePanes;
    onActivate: (kind: PaneKind, key: string) => void;
    onCloseTab: (kind: PaneKind, key: string) => void;
    onCloseGroup: (kind: PaneKind) => void;
    onUpdateTab: (tab: PaneTab) => void;
}

/**
 * One group: its tab strip (each tab closable), then the open tab. On a narrow screen only one group
 * shows at a time (`hiddenOnNarrow` for the others).
 */
const PaneGroupView = ({
    group,
    hiddenOnNarrow,
    onActivate,
    onCloseTab,
    onCloseGroup,
    onUpdateTab,
}: Omit<AssistantSidePanelProps, "panes"> & {
    group: PaneGroup;
    hiddenOnNarrow: boolean;
}) => {
    const { t } = useTranslation();
    const active =
        group.tabs.find((tab) => tab.key === group.activeKey) ?? group.tabs[0];
    const Icon = KIND_ICON[group.kind];
    const stripRef = useRef<HTMLDivElement>(null);

    // The tab in front stays in view, even when the strip scrolls.
    useEffect(() => {
        stripRef.current
            ?.querySelector<HTMLElement>('[aria-selected="true"]')
            ?.scrollIntoView?.({ block: "nearest", inline: "nearest" });
    }, [active.key, group.tabs.length]);

    return (
        <section
            aria-label={t(`vistassistant.sidePanel.${group.kind}`)}
            className={cn(
                "flex min-h-0 flex-1 flex-col overflow-hidden rounded-xl border border-assistant-borderStrong bg-white shadow-[0_1px_3px_rgba(38,23,63,0.06)]",
                hiddenOnNarrow && "max-lg:hidden",
            )}
        >
            <div className="flex h-10 flex-none items-center gap-1 border-b border-assistant-divider px-1.5">
                {/* The tabs shrink like a browser's, down to 72 px; only then the strip scrolls,
                    without a visible bar (it pushed the strip off its line). */}
                <div
                    ref={stripRef}
                    className="flex h-full min-w-0 flex-1 items-center gap-0.5 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
                    role="tablist"
                >
                    {group.tabs.map((tab) => {
                        const isActive = tab.key === active.key;

                        return (
                            <div
                                key={tab.key}
                                className={cn(
                                    "group flex h-7 min-w-[72px] max-w-[200px] flex-[0_1_200px] items-center gap-1.5 rounded-md pl-2 pr-0.5 text-[13px] transition-colors",
                                    isActive
                                        ? "bg-assistant-panel text-primary-darkest"
                                        : "text-neutral-dark hover:bg-assistant-canvas hover:text-primary-darkest",
                                )}
                            >
                                <button
                                    aria-selected={isActive}
                                    className="flex h-full min-w-0 flex-1 items-center gap-1.5"
                                    onClick={() =>
                                        onActivate(group.kind, tab.key)
                                    }
                                    role="tab"
                                    title={tab.title}
                                    type="button"
                                >
                                    <Icon
                                        aria-hidden
                                        className="size-3.5 flex-none"
                                        strokeWidth={1.75}
                                    />
                                    <span className="truncate">
                                        {tab.title}
                                    </span>
                                </button>
                                <button
                                    aria-label={t(
                                        "vistassistant.sidePanel.closeTab",
                                        { title: tab.title },
                                    )}
                                    className={cn(
                                        "flex h-5 w-5 flex-none items-center justify-center rounded hover:bg-white",
                                        // Behind: no room kept for it, it shows on hover or with the
                                        // keyboard focus in the tab (always on a touch screen, which
                                        // has no hover).
                                        !isActive &&
                                            "hidden group-focus-within:flex group-hover:flex [@media(hover:none)]:flex",
                                    )}
                                    onClick={() =>
                                        onCloseTab(group.kind, tab.key)
                                    }
                                    type="button"
                                >
                                    <X aria-hidden className="size-3" />
                                </button>
                            </div>
                        );
                    })}
                </div>
                <button
                    aria-label={t("vistassistant.sidePanel.closeGroup", {
                        group: t(`vistassistant.sidePanel.${group.kind}`),
                    })}
                    className="flex h-7 w-7 flex-none items-center justify-center rounded-md text-neutral-dark hover:bg-assistant-panel hover:text-primary-darkest"
                    onClick={() => onCloseGroup(group.kind)}
                    title={t("vistassistant.sidePanel.closeGroup", {
                        group: t(`vistassistant.sidePanel.${group.kind}`),
                    })}
                    type="button"
                >
                    <X aria-hidden className="size-4" strokeWidth={1.75} />
                </button>
            </div>
            <div className="flex min-h-0 flex-1 flex-col" role="tabpanel">
                {active.kind === "mail" && (
                    <MailPane key={active.key} tab={active} />
                )}
                {active.kind === "event" && (
                    <EventPane
                        key={active.key}
                        onChange={(event) => onUpdateTab({ ...active, event })}
                        tab={active}
                    />
                )}
                {active.kind === "compose" && (
                    <ComposePane
                        key={active.key}
                        onClose={() => onCloseTab("compose", active.key)}
                        tab={active}
                    />
                )}
            </div>
        </section>
    );
};

// Width of the panel, dragged by its left edge and kept in this browser.
const WIDTH_KEY = "mailvista.assistant.sidePanelWidth";
const MIN_WIDTH = 360;
// The chat keeps at least this much room on the left.
const MIN_CHAT_WIDTH = 380;
const KEY_STEP = 32;

const readStoredWidth = (): number | undefined => {
    const stored = Number(readStored(WIDTH_KEY));

    return Number.isFinite(stored) && stored >= MIN_WIDTH ? stored : undefined;
};

const storeWidth = (width: number) =>
    writeStored(WIDTH_KEY, String(Math.round(width)));

/**
 * The assistant's side panel, like Claude's: what was opened from the chat, one card per kind (the
 * mails, the calendar) stacked from top to bottom, each with its tabs. Its left edge is dragged to
 * resize it (or moved with the arrow keys); a double click goes back to the default width.
 *
 * Narrow screen (under 1024 px: tablet, half window, phone): the panel covers the chat and shows one
 * kind at a time, picked in a bar that also leads back to the chat. What is opened from the chat
 * brings the panel back, on its kind.
 */
const AssistantSidePanel = ({ panes, ...actions }: AssistantSidePanelProps) => {
    const { t } = useTranslation();
    const panelRef = useRef<HTMLElement>(null);
    const [width, setWidth] = useState<number | undefined>(readStoredWidth);
    const [focusedKind, setFocusedKind] = useState<PaneKind>();
    const [isNarrowHidden, setIsNarrowHidden] = useState(false);
    const previousPanes = useRef(panes);
    // Re-rendered when the room around the panel changes (opened, window resized): the width kept
    // may no longer fit.
    const [, setContainerWidth] = useState(0);
    const isShown = panes.length > 0;

    useEffect(() => {
        const container = panelRef.current?.parentElement;
        if (!container) return undefined;
        // At once (the first render could not measure), then on every change.
        setContainerWidth(container.clientWidth);
        if (typeof ResizeObserver === "undefined") return undefined;
        const observer = new ResizeObserver(() =>
            setContainerWidth(container.clientWidth),
        );
        observer.observe(container);

        return () => observer.disconnect();
    }, [isShown]);

    // A group opened or changed (a tab opened from the chat, brought to the front): its kind shows,
    // and the panel comes back over the chat. A group only closed changes nothing.
    useEffect(() => {
        const before = previousPanes.current;
        previousPanes.current = panes;
        const changed = panes.find((group) => !before.includes(group));
        if (!changed) return;
        setFocusedKind(changed.kind);
        setIsNarrowHidden(false);
    }, [panes]);

    // The widest the panel may be: its container, less the room kept for the chat.
    const maxWidth = () => {
        const container = panelRef.current?.parentElement;

        return container
            ? Math.max(MIN_WIDTH, container.clientWidth - MIN_CHAT_WIDTH)
            : Number.POSITIVE_INFINITY;
    };
    const clamp = (value: number) =>
        Math.min(Math.max(value, MIN_WIDTH), maxWidth());

    const startResize = (event: ReactPointerEvent<HTMLDivElement>) => {
        const panel = panelRef.current;
        if (!panel) return;
        event.preventDefault();
        const right = panel.getBoundingClientRect().right;
        let last = panel.offsetWidth;
        const move = (moveEvent: PointerEvent) => {
            last = clamp(right - moveEvent.clientX);
            setWidth(last);
        };
        const stop = () => {
            window.removeEventListener("pointermove", move);
            window.removeEventListener("pointerup", stop);
            document.body.style.removeProperty("cursor");
            document.body.style.removeProperty("user-select");
            storeWidth(last);
        };
        document.body.style.cursor = "col-resize";
        document.body.style.userSelect = "none";
        window.addEventListener("pointermove", move);
        window.addEventListener("pointerup", stop);
    };

    const resizeBy = (delta: number) => {
        const next = clamp(
            (width ?? panelRef.current?.offsetWidth ?? 0) + delta,
        );
        setWidth(next);
        storeWidth(next);
    };

    const resetWidth = () => {
        setWidth(undefined);
        removeStored(WIDTH_KEY);
    };

    if (panes.length === 0) return null;

    const shownKind = panes.some((group) => group.kind === focusedKind)
        ? focusedKind
        : panes[panes.length - 1].kind;
    // The width kept, as long as the chat keeps its room (a window made narrower).
    const shownWidth =
        width === undefined ? undefined : Math.min(width, maxWidth());

    return (
        <aside
            ref={panelRef}
            aria-label={t("vistassistant.sidePanel.label")}
            className={cn(
                "relative flex min-w-[360px] flex-none flex-col gap-2 py-2 pl-2.5 pr-2.5",
                shownWidth === undefined && "w-[46%] max-w-[760px]",
                "max-lg:absolute max-lg:inset-0 max-lg:z-30 max-lg:!w-full max-lg:min-w-0 max-lg:max-w-none max-lg:bg-assistant-canvas",
                isNarrowHidden && "max-lg:hidden",
            )}
            style={shownWidth !== undefined ? { width: shownWidth } : undefined}
        >
            <div
                aria-label={t("vistassistant.sidePanel.resize")}
                aria-orientation="vertical"
                aria-valuemin={MIN_WIDTH}
                aria-valuenow={Math.round(
                    width ?? panelRef.current?.offsetWidth ?? 0,
                )}
                className="group/resize absolute inset-y-0 left-0 z-10 flex w-2.5 cursor-col-resize justify-center outline-none max-lg:hidden"
                onDoubleClick={resetWidth}
                onKeyDown={(event) => {
                    if (event.key === "ArrowLeft") resizeBy(KEY_STEP);
                    if (event.key === "ArrowRight") resizeBy(-KEY_STEP);
                }}
                onPointerDown={startResize}
                role="separator"
                tabIndex={0}
                title={t("vistassistant.sidePanel.resize")}
            >
                <span className="my-2 w-0.5 rounded-full bg-transparent transition-colors group-hover/resize:bg-primary-light group-focus-visible/resize:bg-primary-light" />
            </div>
            {/* Narrow screen: back to the chat, and one kind at a time. */}
            <div className="flex flex-none items-center gap-1 lg:hidden">
                <button
                    className="flex h-8 items-center gap-1.5 rounded-lg px-2 text-[13px] font-medium text-primary-darkest hover:bg-assistant-panel"
                    onClick={() => setIsNarrowHidden(true)}
                    type="button"
                >
                    <ArrowLeft
                        aria-hidden
                        className="size-4"
                        strokeWidth={1.75}
                    />
                    {t("vistassistant.sidePanel.backToChat")}
                </button>
                {panes.length > 1 && (
                    <div
                        className="ml-auto flex min-w-0 items-center gap-0.5 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
                        role="tablist"
                    >
                        {panes.map((group) => {
                            const Icon = KIND_ICON[group.kind];

                            return (
                                <button
                                    key={group.kind}
                                    aria-selected={group.kind === shownKind}
                                    className={cn(
                                        "flex h-8 flex-none items-center gap-1.5 rounded-lg px-2.5 text-[13px] transition-colors",
                                        group.kind === shownKind
                                            ? "bg-white font-medium text-primary-darkest shadow-[0_1px_2px_rgba(38,23,63,0.08)]"
                                            : "text-neutral-dark hover:bg-assistant-panel",
                                    )}
                                    onClick={() => setFocusedKind(group.kind)}
                                    role="tab"
                                    type="button"
                                >
                                    <Icon
                                        aria-hidden
                                        className="size-4"
                                        strokeWidth={1.75}
                                    />
                                    {t(`vistassistant.sidePanel.${group.kind}`)}
                                    <span className="text-neutral-dark">
                                        {group.tabs.length}
                                    </span>
                                </button>
                            );
                        })}
                    </div>
                )}
            </div>
            {panes.map((group) => (
                <PaneGroupView
                    key={group.kind}
                    group={group}
                    hiddenOnNarrow={group.kind !== shownKind}
                    {...actions}
                />
            ))}
        </aside>
    );
};

// Protected: typing in the input re-renders the assistant, not the side panel (audit 15/10, P4-05).
export default memo(AssistantSidePanel);
